'use client'

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react'
import {
  EmbeddingContext,
  type EmbeddingContextValue,
  type IndexingStatus,
} from './use-embedding-context'
import { entryToText, contentHash } from './entry-text'
import {
  getAllEmbeddings,
  storeEmbeddings,
  getSchemaVersion,
  setSchemaVersion,
  pruneStaleEmbeddings,
  clearAll,
  SCHEMA_VERSION,
} from './vector-store'
import {
  hybridSearch,
  findSimilarEntries as findSimilarEntriesFn,
  type SearchFilters,
} from './rank'
import type { Entry, AppDocument } from '@/document'
import { useDocumentView, useDocumentStatus } from '@/document'

const BATCH_SIZE = 8
const DEBOUNCE_MS = 2000

/** Check if the connection is slow (2g / slow-2g). */
function isSlowConnection(): boolean {
  if (typeof navigator === 'undefined') return false
  const conn = (
    navigator as Navigator & { connection?: { effectiveType?: string } }
  ).connection
  if (!conn?.effectiveType) return false
  return conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g'
}

interface WorkerMessage {
  type: 'progress' | 'ready' | 'not-ready' | 'embeddings' | 'error'
  data?: unknown
  id?: string
  embeddings?: number[][]
  error?: string
}

export function EmbeddingProvider({ children }: { children: ReactNode }) {
  const doc = useDocumentView()
  const ready = useDocumentStatus()

  const [modelReady, setModelReady] = useState(false)
  const [modelLoading, setModelLoading] = useState(false)
  const [modelProgress, setModelProgress] = useState(0)
  const [indexingStatus, setIndexingStatus] = useState<IndexingStatus>('idle')
  const [indexedCount, setIndexedCount] = useState(0)
  const [totalCount, setTotalCount] = useState(0)

  const workerRef = useRef<Worker | null>(null)
  const pendingRef = useRef<
    Map<
      string,
      { resolve: (v: number[][]) => void; reject: (e: Error) => void }
    >
  >(new Map())
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const indexingRef = useRef(false)

  // Spawn worker on mount
  useEffect(() => {
    if (isSlowConnection()) return

    const worker = new Worker(
      new URL('./embedding-worker.ts', import.meta.url),
      { type: 'module' },
    )
    workerRef.current = worker
    // Capture ref early — by cleanup time, pendingRef.current may differ
    const pendingMap = pendingRef.current

    worker.onmessage = (event: MessageEvent<WorkerMessage>) => {
      const msg = event.data
      switch (msg.type) {
        case 'progress': {
          setModelLoading(true)
          // Worker sends { type: 'progress', data: <transformers.js progress> }
          if (msg.data && typeof msg.data === 'object') {
            const progress = (msg.data as Record<string, unknown>)['progress']
            if (typeof progress === 'number') {
              setModelProgress(Math.round(progress))
            }
          }
          break
        }
        case 'ready': {
          setModelReady(true)
          setModelLoading(false)
          setModelProgress(100)
          break
        }
        case 'not-ready':
          break
        case 'embeddings': {
          if (!msg.id || !msg.embeddings) break
          const pending = pendingRef.current.get(msg.id)
          if (pending) {
            pending.resolve(msg.embeddings)
            pendingRef.current.delete(msg.id)
          }
          break
        }
        case 'error': {
          console.error('[embedding] Worker error:', msg.error)
          if (msg.id) {
            // Per-request error — reject only that request
            const pending = pendingRef.current.get(msg.id)
            if (pending) {
              pending.reject(new Error(msg.error ?? 'Worker error'))
              pendingRef.current.delete(msg.id)
            }
          } else {
            // Global error (model load failure) — mark not ready, reject all
            setModelReady(false)
            setModelLoading(false)
            for (const [, pending] of pendingRef.current) {
              pending.reject(new Error(msg.error ?? 'Worker error'))
            }
            pendingRef.current.clear()
          }
          break
        }
        default:
          break
      }
    }

    worker.onerror = (err) => {
      console.error('[embedding] Worker crash:', err)
      setModelReady(false)
      setModelLoading(false)
    }

    // Trigger model download
    setModelLoading(true)
    worker.postMessage({ type: 'init' })

    return () => {
      worker.terminate()
      workerRef.current = null
      pendingMap.clear()
    }
  }, [])

  // Send embed request to worker, returning a promise
  const embedViaWorker = useCallback((texts: string[]): Promise<number[][]> => {
    const worker = workerRef.current
    if (!worker) return Promise.reject(new Error('Worker not available'))

    const id = crypto.randomUUID()
    return new Promise<number[][]>((resolve, reject) => {
      pendingRef.current.set(id, { resolve, reject })
      worker.postMessage({ type: 'embed', id, texts })
    })
  }, [])

  // Index entries — compare content hashes and embed only changed ones
  const indexEntries = useCallback(
    async (entries: Record<string, Entry>, appDocument: AppDocument) => {
      if (indexingRef.current) return
      indexingRef.current = true
      setIndexingStatus('indexing')

      try {
        // Schema migration — clear all embeddings on version mismatch
        if ((await getSchemaVersion()) !== SCHEMA_VERSION) {
          console.log(
            '[embedding] Schema version mismatch, clearing old embeddings...',
          )
          let cleared = false
          try {
            await clearAll()
            cleared = true
          } catch {
            await new Promise((r) => setTimeout(r, 1000))
            try {
              await clearAll()
              cleared = true
            } catch {
              console.warn(
                '[embedding] Failed to clear old embeddings, deferring to next session',
              )
            }
          }
          if (cleared) {
            await setSchemaVersion(SCHEMA_VERSION)
          } else {
            // Don't mark migration complete — retry next session
            indexingRef.current = false
            setIndexingStatus('idle')
            return
          }
        }

        const allEntries = Object.values(entries).filter((e) => !e.archivedAt)
        const existing = await getAllEmbeddings()
        const existingMap = new Map(
          existing.map((r) => [r.entryId, r.contentHash]),
        )

        // Find entries that need (re-)embedding
        const toEmbed: { entryId: string; text: string; hash: string }[] = []
        for (const entry of allEntries) {
          const text = entryToText(entry, appDocument)
          if (!text) continue
          const hash = contentHash(text)
          if (existingMap.get(entry.id) !== hash) {
            toEmbed.push({ entryId: entry.id, text, hash })
          }
        }

        setTotalCount(toEmbed.length)
        setIndexedCount(0)

        // Process in batches
        for (let i = 0; i < toEmbed.length; i += BATCH_SIZE) {
          const batch = toEmbed.slice(i, i + BATCH_SIZE)
          const texts = batch.map((b) => b.text)
          console.log(
            `[embedding] Embedding batch ${i / BATCH_SIZE + 1} (${batch.length} entries)...`,
          )
          const t0 = performance.now()
          const embeddings = await embedViaWorker(texts)
          console.log(
            `[embedding] Batch done in ${Math.round(performance.now() - t0)}ms, dims=${embeddings[0]?.length}`,
          )

          const records = batch.map((b, idx) => ({
            entryId: b.entryId,
            embedding: embeddings[idx],
            contentHash: b.hash,
          }))
          await storeEmbeddings(records)

          setIndexedCount((prev) => prev + batch.length)

          // Yield to main thread between batches
          if (i + BATCH_SIZE < toEmbed.length) {
            await new Promise((r) => setTimeout(r, 0))
          }
        }

        // Prune embeddings for archived/deleted entries
        const pruned = await pruneStaleEmbeddings(
          new Set(allEntries.map((e) => e.id)),
        )
        if (pruned > 0)
          console.log(`[embedding] Pruned ${pruned} stale embeddings`)

        setIndexingStatus('complete')
      } catch (err) {
        console.error('[embedding] Indexing failed:', err)
        setIndexingStatus('idle')
      } finally {
        indexingRef.current = false
      }
    },
    [embedViaWorker],
  )

  // Initial indexing when model becomes ready + doc is available
  const initialIndexDone = useRef(false)
  useEffect(() => {
    if (!modelReady || !ready || !doc?.entries || initialIndexDone.current)
      return
    initialIndexDone.current = true
    indexEntries(doc.entries, doc)
  }, [modelReady, ready, doc, indexEntries])

  // Watch for doc changes — debounce re-index
  useEffect(() => {
    if (!modelReady || !ready || !doc?.entries || !initialIndexDone.current)
      return

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      indexEntries(doc.entries, doc)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [doc, modelReady, ready, indexEntries])

  // Public embed function
  const embed = useCallback(
    async (texts: string[]): Promise<number[][]> => {
      if (!modelReady) throw new Error('Embedding model not ready')
      return embedViaWorker(texts)
    },
    [modelReady, embedViaWorker],
  )

  // Public search function
  const search = useCallback(
    async (query: string, topK?: number, filters?: SearchFilters) => {
      if (!modelReady || !doc) return []
      const allEmb = await getAllEmbeddings()
      return hybridSearch(query, doc, embedViaWorker, allEmb, filters, topK)
    },
    [modelReady, doc, embedViaWorker],
  )

  // Find entries similar to a given entry
  const findSimilar = useCallback(
    async (entryId: string, topK?: number) => {
      if (!doc) return []
      const allEmb = await getAllEmbeddings()
      return findSimilarEntriesFn(entryId, doc, allEmb, topK)
    },
    [doc],
  )

  const value = useMemo<EmbeddingContextValue>(
    () => ({
      modelReady,
      modelLoading,
      modelProgress,
      indexingStatus,
      indexedCount,
      totalCount,
      embed,
      search,
      findSimilarEntries: findSimilar,
    }),
    [
      modelReady,
      modelLoading,
      modelProgress,
      indexingStatus,
      indexedCount,
      totalCount,
      embed,
      search,
      findSimilar,
    ],
  )

  return (
    <EmbeddingContext.Provider value={value}>
      {children}
    </EmbeddingContext.Provider>
  )
}
